
import React, { useState, useEffect } from 'react';
import { LandingView } from './views/LandingView';
import { MenuView } from './views/MenuView';
import { AdminView } from './views/AdminView';
import { useAuth } from './hooks/useAuth';
import { authService } from './services/auth.service';
import { supabaseService } from './supabaseService';

type Route = 'landing' | 'login' | 'admin' | 'menu' | 'demo';

const App: React.FC = () => {
  const [currentRoute, setCurrentRoute] = useState<Route>('landing');
  const [menuSlug, setMenuSlug] = useState<string>('');
  const { profile, loading, isAuthenticated } = useAuth();
  const [demoUser, setDemoUser] = useState<any>(null);

  useEffect(() => {
    const handleHashChange = async () => {
      const hash = window.location.hash;
      if (hash.startsWith('#/l/')) {
        setMenuSlug(hash.replace('#/l/', ''));
        setCurrentRoute('menu');
      } else if (hash === '#/login') {
        setCurrentRoute('login');
      } else if (hash === '#/admin') {
        setCurrentRoute('admin');
      } else if (hash === '#/demo') {
        // Modo Demo: Busca a primeira empresa ativa para exibição
        const empresas = await supabaseService.getEmpresas();
        const demoTarget = empresas.find(e => e.status === 'ativo') || empresas[0];
        setDemoUser({ 
          nome: 'Visitante Demo', 
          role: 'admin', 
          empresas: demoTarget 
        });
        setCurrentRoute('demo');
      } else {
        setCurrentRoute('landing');
      }
    };

    window.addEventListener('hashchange', handleHashChange);
    handleHashChange();

    return () => window.removeEventListener('hashchange', handleHashChange);
  }, []);

  const navigateTo = (route: Route, slug?: string) => {
    if (slug) setMenuSlug(slug);
    setCurrentRoute(route);
    const hashMap: Record<Route, string> = {
      landing: '#/',
      login: '#/login',
      admin: '#/admin',
      menu: `#/l/${slug}`,
      demo: '#/demo'
    };
    window.location.hash = hashMap[route];
  };

  if (loading) return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-slate-950 text-white">
      <div className="w-12 h-12 border-4 border-orange-600 border-t-transparent rounded-full animate-spin mb-6"></div>
      <div className="flex items-center gap-2 mb-2">
        <div className="w-6 h-6 bg-orange-600 rounded-lg flex items-center justify-center font-black text-xs text-white shadow-lg">D</div>
        <span className="font-black tracking-tight text-xl">Delyon</span>
      </div>
      <p className="text-slate-400 font-bold animate-pulse text-sm">Autenticando sessao...</p>
    </div>
  );

  const isAdminAuthenticated = isAuthenticated && profile;

  switch (currentRoute) {
    case 'demo':
      return <AdminView user={demoUser} isDemo={true} />;
    case 'admin':
      if (!isAdminAuthenticated) return <AuthForm onBack={() => navigateTo('landing')} />;
      return <AdminView user={profile} />;
    case 'menu':
      return <MenuView slug={menuSlug} onBack={() => navigateTo('landing')} />;
    case 'login':
      if (isAdminAuthenticated) { navigateTo('admin'); return null; }
      return <AuthForm onBack={() => navigateTo('landing')} />;
    default:
      return <LandingView onNavigate={navigateTo} />;
  }
};

const AuthForm = ({ onBack }: { onBack: () => void }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrorMsg(null);
    setLoading(true);
    
    try {
      await authService.signIn(email, password);
      // O Context atualizará o estado e o App re-renderizará
    } catch (err: any) {
      setErrorMsg(err.message || "Erro ao fazer login.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 p-6 text-slate-900">
      <div className="bg-white p-10 rounded-[2.5rem] shadow-2xl max-w-md w-full border border-slate-100 animate-fade-in text-slate-900">
        <button onClick={onBack} className="mb-6 text-slate-400 hover:text-slate-900 flex items-center gap-2 font-bold text-sm transition-colors">
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7"></path></svg>
          Voltar
        </button>
        
        <div className="flex items-center gap-2 justify-center mb-10">
          <div className="w-10 h-10 bg-slate-900 rounded-xl flex items-center justify-center text-white font-black shadow-lg">D</div>
          <span className="text-2xl font-black tracking-tight text-slate-900">Delyon</span>
        </div>

        <h2 className="text-2xl font-black text-center mb-2 text-slate-900">Acesse seu painel</h2>
        <p className="text-center text-slate-400 text-sm font-medium mb-8">Gestão SaaS Multi-tenant Profissional</p>
        
        {errorMsg && (
          <div className="mb-6 p-4 bg-red-50 border border-red-100 text-red-600 rounded-2xl text-xs font-bold animate-shake text-center">
            {errorMsg}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-5">
          <div>
            <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2 ml-1">E-mail</label>
            <input 
              required 
              value={email} 
              onChange={e => setEmail(e.target.value)} 
              type="email" 
              placeholder="seu@email.com" 
              className="w-full px-5 py-4 bg-slate-50 border border-slate-200 rounded-2xl focus:ring-2 focus:ring-orange-600 outline-none font-semibold transition-all" 
            />
          </div>

          <div>
            <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2 ml-1">Senha</label>
            <input 
              required 
              value={password} 
              onChange={e => setPassword(e.target.value)} 
              type="password" 
              placeholder="••••••••" 
              className="w-full px-5 py-4 bg-slate-50 border border-slate-200 rounded-2xl focus:ring-2 focus:ring-orange-600 outline-none font-semibold transition-all" 
            />
          </div>
          
          <button disabled={loading} type="submit" className="w-full bg-slate-900 text-white font-black py-5 rounded-2xl hover:bg-orange-600 transition-all shadow-xl flex items-center justify-center gap-3">
            {loading ? <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div> : 'Entrar Agora'}
          </button>
        </form>

        <div className="mt-8 text-center text-slate-300">
          <p className="text-[10px] font-black uppercase tracking-[0.2em]">Acesso Restrito</p>
        </div>
      </div>
      <style>{`
        @keyframes shake {
          0%, 100% { transform: translateX(0); }
          25% { transform: translateX(-5px); }
          75% { transform: translateX(5px); }
        }
        .animate-shake { animation: shake 0.2s ease-in-out 0s 2; }
      `}</style>
    </div>
  );
};

export default App;
